import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Checkbox,
    Avatar,
    Typography,
    IconButton,
    TablePagination,
    Menu,
    MenuItem,
    ListItemIcon,
    Button,
    InputBase
} from '@mui/material';
import {
    MoreVertIcon,
    EditOutlinedIcon,
    DeleteOutlineIcon,
    AddIcon,
    CloseOutlinedIcon,
    SearchOutlinedIcon
} from 'icons/materialIcons';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { deleteSubteam, getSubteamMembers } from 'team/apis/teamSubteamApi';
import { getTeamMembers } from 'team/apis/teamMemberApi';
import { useTeam } from 'team/contexts/teamContext';
import TeamNotificationSnackbar from 'team/shared/components/notification/TeamNotificationSnackbar';
import TeamSelectionBottomBar from 'team/shared/components/notification/TeamSelectionBottomBar';
import TeamAssignRestrictionsModal from './TeamAssignRestrictionsModal';
import Modal from 'components/common/Modal';
import TeamWhiteButtonWithIcon from 'team/shared/components/button/TeamWhiteButtonWithIcon';

import { getSubteamColor, getSubteamInitials } from 'team/shared/utils/subteamUtils';

const TeamSubteamsTable = ({
    subteams = [],
    onSubteamClick,
    isLoading = false,
    onSelectSubteams,
    selectedSubteamIds = [],
    onSubteamRemoved
}) => {
    const { currentTeamId } = useTeam();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selected, setSelected] = useState(selectedSubteamIds || []);
    const [optionsMenuAnchorEl, setOptionsMenuAnchorEl] = useState(null);
    const [activeSubteamForOptions, setActiveSubteamForOptions] = useState(null);
    const [hoveredRow, setHoveredRow] = useState(null);
    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'warning'
    });
    const [isAssignRestrictionsModalOpen, setIsAssignRestrictionsModalOpen] = useState(false);
    const [removeModalOpen, setRemoveModalOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [subteamToRemove, setSubteamToRemove] = useState(null);
    const [addAdminModalOpen, setAddAdminModalOpen] = useState(false);
    const [addAdminModalAnchorEl, setAddAdminModalAnchorEl] = useState(null);
    const [subteamForAdmin, setSubteamForAdmin] = useState(null);
    const [subteamMembers, setSubteamMembers] = useState([]);
    const [memberSearchQuery, setMemberSearchQuery] = useState('');
    const [loadingMembers, setLoadingMembers] = useState(false);

    const searchInputRef = useRef(null);

    // Focus the input after menu opens
    useEffect(() => {
        if (addAdminModalOpen && searchInputRef.current) {
            const timer = setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [addAdminModalOpen]);

    // Use provided subteams (empty state should be handled by parent component)
    const displaySubteams = subteams;

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelected = displaySubteams.map((subteam) => subteam._id);
            setSelected(newSelected);
            if (onSelectSubteams) {
                onSelectSubteams(newSelected);
            }
        } else {
            setSelected([]);
            if (onSelectSubteams) {
                onSelectSubteams([]);
            }
        }
    };

    const handleSelectClick = (event, subteamId) => {
        event.stopPropagation();
        const selectedIndex = selected.indexOf(subteamId);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, subteamId);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1)
            );
        }

        setSelected(newSelected);
        if (onSelectSubteams) {
            onSelectSubteams(newSelected);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleOptionsButtonClick = (event, subteam) => {
        event.stopPropagation();
        setOptionsMenuAnchorEl(event.currentTarget);
        setActiveSubteamForOptions(subteam);
    };

    const handleOptionsMenuClose = () => {
        setOptionsMenuAnchorEl(null);
        setActiveSubteamForOptions(null);
    };

    const handleEdit = () => {
        if (activeSubteamForOptions && onSubteamClick) {
            onSubteamClick(activeSubteamForOptions);
        }
        handleOptionsMenuClose();
    };

    const handleDelete = () => {
        // Store the subteam before closing the menu
        if (activeSubteamForOptions) {
            setSubteamToRemove(activeSubteamForOptions);
        }
        handleOptionsMenuClose();
        setRemoveModalOpen(true);
    };

    const handleRemoveModalClose = () => {
        if (!isDeleting) {
            setRemoveModalOpen(false);
            setSubteamToRemove(null);
        }
    };

    const handleConfirmRemove = async () => {
        if (!subteamToRemove || !subteamToRemove._id || isDeleting) {
            console.log('[TeamSubteamsTable] Early return from handleConfirmRemove', {
                subteamToRemove,
                isDeleting
            });
            return;
        }

        setIsDeleting(true);
        try {
            console.log('[TeamSubteamsTable] Calling deleteSubteam with ID:', subteamToRemove._id);
            const response = await deleteSubteam(subteamToRemove._id);
            console.log('[TeamSubteamsTable] deleteSubteam response:', response);

            if (response && response.success) {
                setNotification({
                    open: true,
                    message: 'Subteam removed successfully',
                    severity: 'success'
                });

                // Call the callback to refresh the subteams list
                if (onSubteamRemoved) {
                    console.log('[TeamSubteamsTable] Calling onSubteamRemoved callback');
                    onSubteamRemoved();
                }

                // Close modal and clear the subteam reference
                setSubteamToRemove(null);
                setTimeout(() => {
                    setRemoveModalOpen(false);
                }, 500);
            } else {
                throw new Error(response?.error || 'Failed to delete subteam');
            }
        } catch (error) {
            console.error('[TeamSubteamsTable] Error deleting subteam:', error);
            setNotification({
                open: true,
                message: error.message || 'Failed to remove subteam. Please try again.',
                severity: 'error'
            });
        } finally {
            setIsDeleting(false);
        }
    };

    const handleAddAdminClick = async (e, subteam) => {
        e.stopPropagation();

        // Check if subteam has members
        if (!subteam.memberCount || subteam.memberCount === 0) {
            setNotification({
                open: true,
                message: 'Add members to this Subteam before assigning a Subteam admin.',
                severity: 'warning'
            });
            return;
        }

        setSubteamForAdmin(subteam);
        setAddAdminModalAnchorEl(e.currentTarget);
        setAddAdminModalOpen(true);
        setMemberSearchQuery('');

        // Fetch subteam members
        setLoadingMembers(true);
        let subteamMembersList = [];
        try {
            const response = await getSubteamMembers(subteam._id);
            console.log('[TeamSubteamsTable] Subteam members response:', response);

            // Handle different response structures
            if (response) {
                if (Array.isArray(response)) {
                    subteamMembersList = response;
                } else if (response.members) {
                    subteamMembersList = response.members;
                } else if (response.success && response.members) {
                    subteamMembersList = response.members;
                } else if (response.data && Array.isArray(response.data)) {
                    subteamMembersList = response.data;
                } else if (response.data && response.data.members) {
                    subteamMembersList = response.data.members;
                }
            }
            console.log('[TeamSubteamsTable] Setting subteamMembers:', subteamMembersList);
            setSubteamMembers(subteamMembersList);
        } catch (error) {
            console.warn('[TeamSubteamsTable] getSubteamMembers failed, using getTeamMembers with subteamId filter:', error);
            // Fallback: Use getTeamMembers with subteamId filter
            try {
                if (currentTeamId) {
                    const teamMembersResponse = await getTeamMembers(currentTeamId, {
                        status: 'active',
                        subteamId: subteam._id
                    });
                    subteamMembersList = teamMembersResponse?.members || [];
                    console.log('[TeamSubteamsTable] Fallback: Setting subteamMembers from getTeamMembers:', subteamMembersList);
                    setSubteamMembers(subteamMembersList);
                } else {
                    setSubteamMembers([]);
                }
            } catch (fallbackError) {
                console.error('[TeamSubteamsTable] Error fetching team members as fallback:', fallbackError);
                setSubteamMembers([]);
                setNotification({
                    open: true,
                    message: 'Failed to load subteam members',
                    severity: 'error'
                });
            }
        } finally {
            setLoadingMembers(false);
        }
    };

    const handleAddAdminModalClose = () => {
        setAddAdminModalOpen(false);
        setAddAdminModalAnchorEl(null);
        setSubteamForAdmin(null);
        setMemberSearchQuery('');
        setSubteamMembers([]);
    };

    const handleMemberSelect = (member) => {
        // TODO: Implement add admin functionality
        console.log('Selected member to add as admin:', member);
        handleAddAdminModalClose();
    };

    // Filter members based on search query
    console.log('=== CALCULATING FILTERED MEMBERS ===');
    console.log('memberSearchQuery:', `"${memberSearchQuery}"`);
    console.log('subteamMembers.length:', subteamMembers.length);

    const filteredMembers = subteamMembers.filter(member => {
        if (!memberSearchQuery) return true;
        const query = memberSearchQuery.toLowerCase();
        const fullName = member.user?.fullName || member.fullName || '';
        const matches = fullName.toLowerCase().includes(query);

        if (matches) {
            console.log(`✓ Match found: ${fullName} (query: "${query}")`);
        }

        return matches;
    });

    console.log('filteredMembers.length:', filteredMembers.length);
    console.log('=== FILTERED MEMBERS CALCULATED ===');

    const isSelected = (subteamId) => selected.indexOf(subteamId) !== -1;
    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - displaySubteams.length) : 0;

    return (
        <Box sx={{ width: '100%', overflow: 'auto' }}>
            <Paper
                sx={{
                    width: '100%',
                    mt: 2,
                    borderRadius: '10px',
                    overflow: 'hidden',
                    minWidth: '1000px',
                    backgroundColor: 'white',
                    boxShadow: 'none',
                    border: '1px solid #f2f2f2'
                }}
            >
                <TableContainer>
                    <Table
                        aria-labelledby="tableTitle"
                        sx={{
                            minWidth: 1000,
                            tableLayout: 'fixed',
                            '& .MuiTableCell-root': {
                                borderColor: '#f2f2f2'
                            }
                        }}
                    >
                        <TableHead>
                            <TableRow
                                sx={{
                                    backgroundColor: 'white',
                                    borderBottom: '1px solid #f2f2f2',
                                    '& th': {
                                        borderBottom: '1px solid #f2f2f2',
                                        backgroundColor: 'white',
                                        color: '#818181',
                                        fontWeight: 600
                                    }
                                }}
                            >
                                <TableCell padding="none" sx={{ width: '48px', paddingLeft: '16px' }}>
                                    <Checkbox
                                        indeterminate={selected.length > 0 && selected.length < displaySubteams.length}
                                        checked={displaySubteams.length > 0 && selected.length === displaySubteams.length}
                                        onChange={handleSelectAllClick}
                                        inputProps={{ 'aria-label': 'select all subteams' }}
                                        sx={{
                                            color: '#e0e0e0',
                                            padding: '4px',
                                            '&.Mui-checked': {
                                                color: '#000',
                                            },
                                            '&.MuiCheckbox-indeterminate': {
                                                color: '#000',
                                            },
                                            '& .MuiSvgIcon-root': {
                                                borderRadius: '0.375rem',
                                            },
                                        }}
                                    />
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#818181', paddingLeft: '12px', paddingRight: '0px', width: '50%' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#818181', whiteSpace: 'nowrap' }}>
                                        Select all
                                    </Typography>
                                </TableCell>
                                <TableCell align="center" sx={{ fontWeight: 600, color: '#818181', minWidth: '180px', paddingLeft: '160px', paddingRight: '20px', whiteSpace: 'nowrap', textAlign: 'center' }}>Subteam Admins</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 600, color: '#818181', minWidth: '180px', paddingLeft: '80px', whiteSpace: 'nowrap' }}>Members</TableCell>
                                <TableCell sx={{ width: '40px', padding: 0 }}></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody
                            sx={{
                                '& tr:last-child td': {
                                    borderBottom: 'none !important'
                                }
                            }}
                        >
                            {displaySubteams
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((subteam, index) => {
                                    const isItemSelected = isSelected(subteam._id);
                                    const totalRowsInPage = displaySubteams.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).length;
                                    const isLastRow = index === totalRowsInPage - 1 && emptyRows === 0;

                                    return (
                                        <TableRow
                                            key={subteam._id}
                                            selected={isItemSelected}
                                            onClick={() => onSubteamClick && onSubteamClick(subteam)}
                                            onMouseEnter={() => setHoveredRow(subteam._id)}
                                            onMouseLeave={() => setHoveredRow(null)}
                                            sx={{
                                                cursor: 'pointer',
                                                '&.Mui-selected': {
                                                    backgroundColor: '#f0f0f0'
                                                },
                                                '& td': {
                                                    borderBottom: isLastRow ? 'none !important' : '1px solid #f2f2f2'
                                                }
                                            }}
                                        >
                                            <TableCell padding="none" sx={{ width: '48px', paddingLeft: '16px' }}>
                                                <Checkbox
                                                    checked={isItemSelected}
                                                    onClick={(e) => handleSelectClick(e, subteam._id)}
                                                    inputProps={{ 'aria-labelledby': `subteam-${subteam._id}` }}
                                                    sx={{
                                                        color: '#e0e0e0',
                                                        padding: '4px',
                                                        '&.Mui-checked': {
                                                            color: '#000',
                                                        },
                                                        '& .MuiSvgIcon-root': {
                                                            borderRadius: '0.375rem',
                                                        },
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ paddingLeft: '12px', paddingRight: 0 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                                    <Avatar
                                                        src={subteam.logoUrl}
                                                        alt={subteam.name}
                                                        sx={{
                                                            width: 40,
                                                            height: 40,
                                                            backgroundColor: subteam.logoUrl ? 'transparent' : getSubteamColor(subteam._id, subteam.name),
                                                            border: subteam.logoUrl ? 'none' : 'none',
                                                            flexShrink: 0
                                                        }}
                                                    >
                                                        {!subteam.logoUrl && getSubteamInitials(subteam.name)}
                                                    </Avatar>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontWeight: 700,
                                                                color: '#4f4f4f',
                                                                fontSize: '1rem',
                                                                textDecoration: 'none'
                                                            }}
                                                        >
                                                            {subteam.name}
                                                        </Typography>
                                                        {subteam.description && (
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: '#757575',
                                                                    fontSize: '0.75rem',
                                                                    textDecoration: 'none',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    display: '-webkit-box',
                                                                    WebkitLineClamp: 2,
                                                                    WebkitBoxOrient: 'vertical'
                                                                }}
                                                            >
                                                                {subteam.description}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="center" sx={{ paddingLeft: '160px', paddingRight: '20px', textAlign: 'center' }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                                                    <Button
                                                        variant="contained"
                                                        onClick={(e) => handleAddAdminClick(e, subteam)}
                                                        sx={{
                                                            backgroundColor: 'white',
                                                            color: '#4f4f4f',
                                                            borderRadius: '1.25rem',
                                                            border: '0.0625rem solid #e3e3e3',
                                                            boxShadow: 'none',
                                                            textTransform: 'none',
                                                            fontSize: '0.7rem',
                                                            fontWeight: '600',
                                                            padding: '0.5rem 1rem',
                                                            minWidth: 'auto',
                                                            whiteSpace: 'nowrap',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1,
                                                            '&:hover': {
                                                                backgroundColor: 'white',
                                                                boxShadow: 2,
                                                            }
                                                        }}
                                                    >
                                                        <GroupOutlinedIcon
                                                            sx={{
                                                                fontSize: '1rem',
                                                                color: '#bababa'
                                                            }}
                                                        />
                                                        <Box
                                                            sx={{
                                                                backgroundColor: '#f7f7f7',
                                                                borderRadius: '50%',
                                                                width: '20px',
                                                                height: '20px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center'
                                                            }}
                                                        >
                                                            <AddIcon
                                                                sx={{
                                                                    fontSize: '0.875rem',
                                                                    color: '#b4b4b4'
                                                                }}
                                                            />
                                                        </Box>
                                                    </Button>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="center" sx={{ paddingLeft: '80px' }}>
                                                {subteam.memberCount > 0 ? (
                                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                                                        <Button
                                                            variant="contained"
                                                            sx={{
                                                                backgroundColor: '#f2f2f2',
                                                                color: '#4f4f4f',
                                                                borderRadius: '1.25rem',
                                                                border: 'none',
                                                                boxShadow: 'none',
                                                                textTransform: 'none',
                                                                fontSize: '0.7rem',
                                                                fontWeight: '600',
                                                                padding: '0.5rem 1rem',
                                                                minWidth: 'auto',
                                                                whiteSpace: 'nowrap',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1,
                                                                '&:hover': {
                                                                    backgroundColor: '#f2f2f2',
                                                                    boxShadow: 2,
                                                                }
                                                            }}
                                                        >
                                                            <PersonOutlineIcon
                                                                sx={{
                                                                    fontSize: '1rem',
                                                                    color: '#bababa'
                                                                }}
                                                            />
                                                            {subteam.memberCount}
                                                        </Button>
                                                    </Box>
                                                ) : (
                                                    <TeamWhiteButtonWithIcon
                                                        text="Add Members"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            // Navigate to subteam members page when clicking "Add Member"
                                                            if (onSubteamClick) {
                                                                onSubteamClick(subteam);
                                                            }
                                                        }}
                                                        padding="0.5rem 1rem"
                                                        sx={{
                                                            minWidth: 'auto',
                                                            whiteSpace: 'nowrap',
                                                            border: '0.0625rem solid #e3e3e3',
                                                        }}
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ padding: 0, width: '40px' }}>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => handleOptionsButtonClick(e, subteam)}
                                                    sx={{
                                                        backgroundColor: '#f5f5f5',
                                                        borderRadius: '20px',
                                                        padding: '8px',
                                                        opacity: hoveredRow === subteam._id ? 1 : 0,
                                                        transition: 'opacity 0.2s',
                                                        '&:hover': {
                                                            backgroundColor: '#e0e0e0'
                                                        }
                                                    }}
                                                >
                                                    <MoreVertIcon sx={{ fontSize: 18, color: '#828282' }} />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            {emptyRows > 0 && (
                                <TableRow
                                    style={{ height: 53 * emptyRows }}
                                    sx={{
                                        '& td': {
                                            borderBottom: 'none'
                                        }
                                    }}
                                >
                                    <TableCell colSpan={5} sx={{ borderBottom: 'none' }} />
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                {displaySubteams.length > rowsPerPage && (
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={displaySubteams.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{
                            borderTop: '1px solid #f2f2f2',
                            backgroundColor: 'white',
                            borderBottom: 'none',
                            '& .MuiTablePagination-toolbar': {
                                paddingLeft: '16px',
                                paddingRight: '16px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            },
                            '& .MuiTablePagination-spacer': {
                                display: 'none'
                            },
                            '& .MuiTablePagination-selectLabel': {
                                margin: 0,
                                marginRight: '8px',
                                order: 1
                            },
                            '& .MuiTablePagination-select': {
                                order: 2
                            },
                            '& .MuiTablePagination-displayedRows': {
                                margin: 0,
                                marginLeft: '8px',
                                order: 3
                            },
                            '& .MuiTablePagination-actions': {
                                marginLeft: 'auto',
                                order: 4
                            }
                        }}
                    />
                )}
            </Paper>

            {/* Options Menu */}
            <Menu
                anchorEl={optionsMenuAnchorEl}
                open={Boolean(optionsMenuAnchorEl)}
                onClose={handleOptionsMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <MenuItem onClick={handleEdit}>
                    <ListItemIcon>
                        <EditOutlinedIcon fontSize="small" sx={{ color: '#828282' }} />
                    </ListItemIcon>
                    <Typography variant="body2">Edit Subteam</Typography>
                </MenuItem>
                <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
                    <ListItemIcon>
                        <DeleteOutlineIcon fontSize="small" sx={{ color: 'error.main' }} />
                    </ListItemIcon>
                    <Typography variant="body2" sx={{ color: 'error.main' }}>Remove</Typography>
                </MenuItem>
            </Menu>

            {/* Add Subteam Admin Member Selection Menu */}
            {addAdminModalAnchorEl && (
                <Menu
                    anchorEl={addAdminModalAnchorEl}
                    open={addAdminModalOpen}
                    onClose={handleAddAdminModalClose}
                    disableAutoFocusItem={true}
                    disableAutoFocus={true}
                    disableEnforceFocus={true}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left'
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left'
                    }}
                    PaperProps={{
                        sx: {
                            borderRadius: '12px',
                            boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.15)',
                            width: {
                                xs: '70vw',
                                sm: '320px',
                                md: '350px'
                            },
                            maxWidth: '350px',
                            maxHeight: '80vh',
                            overflow: 'hidden',
                            marginTop: '12px',
                            padding: '12px',
                            backgroundColor: '#fafafa'
                        }
                    }}
                    MenuListProps={{
                        sx: {
                            padding: 0
                        },
                        disablePadding: true,
                        autoFocusItem: false
                    }}
                    slotProps={{
                        backdrop: {
                            sx: {
                                cursor: 'default'
                            }
                        }
                    }}
                >
                    {console.log('>>> MENU IS RENDERING <<<')}
                    {console.log('State: query =', `"${memberSearchQuery}"`, ', filtered =', filteredMembers.length)}

                    {/* Search Bar */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            backgroundColor: 'white',
                            borderRadius: '20px',
                            border: '1px solid #e3e3e3',
                            padding: '8px 16px',
                            marginBottom: '12px',
                            '&:hover': {
                                boxShadow: 2,
                            },
                        }}
                    >
                        <SearchOutlinedIcon
                            sx={{
                                color: '#828282',
                                fontSize: '18px',
                                marginRight: '8px'
                            }}
                        />
                        <InputBase
                            fullWidth
                            inputRef={searchInputRef}
                            value={memberSearchQuery}
                            onChange={(e) => {
                                const newValue = e.target.value;
                                console.log('>>> INPUT CHANGE EVENT <<<');
                                console.log('New value:', `"${newValue}"`);
                                setMemberSearchQuery(newValue);
                            }}
                            onKeyDown={(e) => {
                                e.stopPropagation();
                            }}
                            onBlur={(e) => {
                                e.preventDefault();
                            }}
                            placeholder="Search..."
                            sx={{
                                fontSize: '0.7rem',
                                color: '#4f4f4f',
                                '& .MuiInputBase-input::placeholder': {
                                    color: '#828282',
                                    opacity: 1,
                                },
                            }}
                        />
                    </Box>

                    <Box sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
                        {console.log('>>> RENDERING MEMBER LIST <<<')}
                        {console.log('Loading:', loadingMembers, 'FilteredMembers.length:', filteredMembers.length)}

                        {loadingMembers ? (
                            <>
                                {console.log('Showing loading state')}
                                <Box sx={{ padding: 2, textAlign: 'center' }}>
                                    <Typography sx={{ fontSize: '0.875rem', color: '#828282' }}>
                                        Loading members...
                                    </Typography>
                                </Box>
                            </>
                        ) : filteredMembers.length === 0 ? (
                            <>
                                {console.log('Showing empty state')}
                                <Box sx={{ padding: 2, textAlign: 'center' }}>
                                    <Typography sx={{ fontSize: '0.875rem', color: '#828282' }}>
                                        {memberSearchQuery ? 'No members found' : 'No members in this subteam'}
                                    </Typography>
                                </Box>
                            </>
                        ) : (
                            <>
                                {console.log('Rendering member items, count:', filteredMembers.length)}
                                {filteredMembers.map((member, index) => {
                                    const memberId = member.userId || member.user?._id || member._id;
                                    const fullName = member.user?.fullName || member.fullName || 'Unknown';
                                    const email = member.user?.email || member.email || '';
                                    const profilePicture = member.user?.profilePictureUrl || member.profilePictureUrl;

                                    console.log(`Rendering member ${index + 1}:`, fullName);

                                    return (
                                        <MenuItem
                                            key={memberId || member._id}
                                            onClick={() => handleMemberSelect(member)}
                                            sx={{
                                                borderRadius: '8px',
                                                marginBottom: '4px',
                                                '&:hover': {
                                                    backgroundColor: '#f0f0f0'
                                                }
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                                <Avatar
                                                    src={profilePicture}
                                                    alt={fullName}
                                                    sx={{
                                                        width: 40,
                                                        height: 40,
                                                        backgroundColor: '#e0e0e0'
                                                    }}
                                                >
                                                    {fullName.charAt(0).toUpperCase()}
                                                </Avatar>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#333' }}>
                                                        {fullName}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </MenuItem>
                                    );
                                })}
                            </>
                        )}
                    </Box>
                    {console.log('>>> MENU RENDER COMPLETE <<<')}
                </Menu>
            )}

            {/* Notification Snackbar */}
            <TeamNotificationSnackbar
                open={notification.open}
                message={notification.message}
                severity={notification.severity}
                handleClose={() => setNotification({ ...notification, open: false })}
            />

            {/* Bottom Selection Bar */}
            <TeamSelectionBottomBar
                open={selected.length > 0}
                selectedCount={selected.length}
                itemName="Subteam"
                actionText="Assign Restrictions"
                onAction={() => {
                    setIsAssignRestrictionsModalOpen(true);
                }}
                onClose={() => {
                    setSelected([]);
                    if (onSelectSubteams) {
                        onSelectSubteams([]);
                    }
                }}
            />

            {/* Assign Restrictions Modal */}
            <TeamAssignRestrictionsModal
                open={isAssignRestrictionsModalOpen}
                onClose={() => {
                    setIsAssignRestrictionsModalOpen(false);
                    // Clear selection after modal closes
                    setSelected([]);
                    if (onSelectSubteams) {
                        onSelectSubteams([]);
                    }
                }}
                selectedSubteams={displaySubteams.filter(subteam => selected.includes(subteam._id))}
                onSuccess={(count) => {
                    // Show success snackbar in main page after modal closes
                    setTimeout(() => {
                        setNotification({
                            open: true,
                            message: `${count} Subteam${count !== 1 ? 's' : ''} updated`,
                            severity: 'success'
                        });
                    }, 300); // Small delay to ensure modal is closed
                }}
            />

            {/* Remove Subteam Confirmation Modal */}
            <Modal
                open={removeModalOpen}
                onClose={handleRemoveModalClose}
                width={{ xs: '90%', sm: '60%', md: '40%' }}
                height="auto"
                borderRadius={8}
            >
                <Box sx={{ padding: 0, position: 'relative' }}>
                    {/* Header */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: { xs: 2, sm: 3 },
                            paddingBottom: { xs: 1, sm: 1.5 }
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                fontSize: { xs: '1.1rem', sm: '1.4rem' },
                                color: '#333'
                            }}
                        >
                            Remove Subteam
                        </Typography>
                        <IconButton
                            onClick={handleRemoveModalClose}
                            disabled={isDeleting}
                            sx={{
                                color: '#828282',
                                '&:hover': {
                                    backgroundColor: '#f5f5f5'
                                },
                                '&:disabled': {
                                    opacity: 0.5
                                }
                            }}
                        >
                            <CloseOutlinedIcon sx={{ fontSize: '1.5rem' }} />
                        </IconButton>
                    </Box>

                    {/* Content */}
                    <Box sx={{ padding: { xs: 2, sm: 3 } }}>
                        <Typography
                            sx={{
                                fontSize: '0.875rem',
                                color: '#4f4f4f',
                                marginBottom: '2rem',
                                lineHeight: 1.5
                            }}
                        >
                            Are you sure you want to remove this Subteam? This Subteam will be permanently deleted. All the members within it will remain in your Team.
                        </Typography>

                        {/* Buttons */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                            <Button
                                variant="outlined"
                                onClick={handleRemoveModalClose}
                                disabled={isDeleting}
                                sx={{
                                    borderColor: '#e0e0e0',
                                    color: '#333',
                                    textTransform: 'none',
                                    padding: '10px 24px',
                                    borderRadius: '1.5rem',
                                    '&:hover': {
                                        borderColor: '#b0b0b0',
                                        backgroundColor: '#f5f5f5'
                                    },
                                    '&:disabled': {
                                        opacity: 0.5
                                    }
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleConfirmRemove();
                                }}
                                disabled={isDeleting}
                                sx={{
                                    backgroundColor: '#000',
                                    color: '#fff',
                                    textTransform: 'none',
                                    padding: '10px 24px',
                                    borderRadius: '1.5rem',
                                    '&:hover': {
                                        backgroundColor: '#333',
                                    },
                                    '&:disabled': {
                                        backgroundColor: '#e0e0e0',
                                        color: '#9e9e9e'
                                    }
                                }}
                            >
                                {isDeleting ? 'Removing...' : 'Yes, remove'}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
};

export default TeamSubteamsTable;